import { VatService } from "@/services/vat-service";
import { ProductEditRowState } from "../products-table/product-edit-row-state";
import { SaleEditState } from "./sale-edit-state";
import { Product, SaleRow, SizeTableEntry } from "@/models/base";
import {i18n} from "@/setup/i18n-setup";
import {AuthService, DossierConfigService} from "@/services";
import { SizeTableService } from "@/services/size-table-service";
import { MODULE } from "@/config/modules";
import { roundToDecimals } from "../numbers";

export class SaleEditRowState extends ProductEditRowState {
	public amount1:number = 1;
	public amount2:number = 1;
	private state:SaleEditState;
	public overrideMobileModalOpen:boolean = false;
	public ledgerAccountCode:string = "";
	public productSkuChanged:boolean = false;
	public dontUseLeeggoed:boolean = false;
	public trackingNumber:string = "";
	public trackingNumberEditModal:boolean = false;
	public sizeTableEntryID:number | null = null;
	public sizeEntries:SizeTableEntry[] = [];

	public constructor(state:SaleEditState){
		super();
		this.state = state;
	}

	// Loads the size options for the current product (module "Matentabel"). Clears them otherwise.
	public async loadSizeEntries(){
		if (!this.product || !this.product.SizeTableID || !AuthService.hasModule(MODULE.MATENTABEL)) {
			this.sizeEntries = [];
			return;
		}
		let table = await SizeTableService.getById(this.product.SizeTableID);
		this.sizeEntries = table.getSortedEntries();
	}


	setVatByRegime() {
		if (this.state.sale.VatRegime == 1 || this.state.sale.VatRegime == 7) {
			if(DossierConfigService.getContactsConfig().ContactSelectVatID){
				if(this.state.sale.Contact && this.state.sale.Contact?.DefaultVatID!=null){
					this.vatItem = VatService.getVatById(this.state.sale.Contact?.DefaultVatID);
				}
				return;
			}
			if (this.product && this.product.Vat && this.product.ID != 0) {
				this.vatItem = this.product.Vat;
				return;
			}
			this.vatItem = VatService.getVatByValue(this.state.selectedRow.Vat);
			return;
		}
		let vatRegime = VatService.getVatRegime(this.state.sale.VatRegime);
		this.vatItem = VatService.getVatByValue(vatRegime.getSaleVat());
	}

	onProductDeselected(){
		if (this.product){
			this.productSkuChanged = true;
		}
		this.product = null;
	}

	// eslint-disable-next-line max-lines-per-function
	async onProductSelected(product:Product){
		if (!product.Active) {
			let message = i18n.t("components.sales.warning-product-is-disabled");
			await this.state.vue.$wf.dialog(message, i18n.t("common.ok"), i18n.t("common.warning"), "warning");
		}
		this.product = product;
		this.productDescription = product.getName(this.state.sale.getLng());
		this.trackingNumber = "";
		this.sizeTableEntryID = null;
		await this.loadSizeEntries();
		// When the product was resolved by scanning a per-size barcode, preselect that size.
		if (product.MatchedSizeTableEntryID && this.sizeEntries.some(e => e.ID === product.MatchedSizeTableEntryID)) {
			this.sizeTableEntryID = product.MatchedSizeTableEntryID;
		}
		this.ledgerAccountCode = product.LedgerAccountCode || "";
		this.unitPrice = 0;
		this.setAmount2(product.DefaultValueQty2);

		this.setVatByRegime();

		if (product.Unit != null){
			this.unit = product.Unit.Name.getTranslation(this.state.sale.getLng());
		}

		if (this.state.isPurchaseType) {
			let supplierFound = false;
			for (let supplier of product.Suppliers) {
				if (supplier.ContactID != this.state.sale.ContactID){
					continue;
				}
				supplierFound = true;
				if(this.currency > 1){
					this.unitPrice = supplier.NettoPrice / this.currencyRate;
				}else{
					this.unitPrice = supplier.NettoPrice;
				}
				let row = new SaleRow(this.state.selectedRow.getJSON());
				this.setRowValues(row);
				row.calculateTotals(this.state.sale);
				let discount = this.state.computeRowDiscount(row);
				if(product.DiscountsDisabled){
					discount = 0;
				}
				if(discount != 0){
					this.discount = discount;
				}
				if(this.discount!=0){
					this.unitPrice = supplier.CatalogPrice;
					this.setDiscount(discount);
				}
				break;
			}

			if(this.unitPrice == 0 && !supplierFound && product.Suppliers.length > 0){
				this.unitPrice = product.Suppliers[0].NettoPrice;
			}
			this.setNetto(0, true);

		}else{
			if (product.BuyNettoPrice != null){
				this.unitBuyPrice = product.BuyNettoPrice;
			}else{
				let mainSupplier = product.getMainSupplier();
				if (mainSupplier) {
					this.unitBuyPrice = mainSupplier.NettoPrice;
				} else {
					this.unitBuyPrice = 0.0;
				}
			}
			this.calculateRowUnitPrice();
		}

	}

	public calculateRowUnitPrice(){
		let row = new SaleRow(this.state.selectedRow.getJSON());
		this.setRowValues(row);
		row.calculateTotals(this.state.sale);

		//Contact price rules
		let discount = this.state.computeRowDiscount(row);
		if(this.product && this.product.DiscountsDisabled){
			discount = 0;
		}
		this.setDiscount(discount);
		let unitPrice = this.state.computeRowUnitPrice(row);
		if (this.product){
			if (!this.state.userConfig.getSaleConfig(this.state.sale.TypeID).DontUseLeeggoed) {
				this.setLeeggoedPrice(this.product.LeeggoedTax);
			}
			this.setEcoboniTax(this.product.EcoboniTax);
			this.setAccijnzenTax(this.product.AccijnzenTax);
			this.setCustomVatExclusiveTax(this.product.CustomVatExclusiveTax);
			this.setCustomVatInclusiveTax(this.product.CustomVatInclusiveTax);
		}
		if(this.currency>1){
			this.setUnitMcPrice(unitPrice);
		}else{
			this.setUnitPrice(unitPrice);
		}
	}


	focusSku(){
		this.emit("focusSku");
	}

	focusQty(){
		this.emit("focusQty");
	}

	clearSku(){
		this.emit("clearSku");
	}

	private setAmountRunRCalculateRowUnitPrice(){
		if ((this.product && this.product.PriceRules.length > 0) || this.state.generalPriceRules.length > 0) {
			this.calculateRowUnitPrice();
		}
	}

	setAmount1(value:number){
		this.amount1 = value;
		this.amount = this.calculateAmount();
		this.setAmountRunRCalculateRowUnitPrice();
	}

	setAmount2(value:number){
		this.amount2 = value;
		this.amount = this.calculateAmount();
		this.setAmountRunRCalculateRowUnitPrice();
	}

	setDuration(value: number) {
		super.setDuration(value);
		this.amount1 = this.amount;
		this.amount = this.calculateAmount();
	}

	private calculateAmount():number {
		if (DossierConfigService.getSaleConfigs().UseAmount2AsMultiplier) {
			return this.amount1 * this.amount2;
		}
		return this.amount1;
	}

	getVat():number{
		if (this.state.sale.Contact && this.state.sale.Contact.GroupID != 2 && this.state.sale.VatRegime == 7) {
			let roundedProfit = roundToDecimals(this.netto - (this.unitBuyPrice * this.amount), 2);
			return roundedProfit * (this.vatMultiplier - 1);
		}
		return super.getVat();
	}

	getVatFromBruto(bruto:number):number {
		if (this.state.sale.Contact && this.state.sale.Contact.GroupID != 2 && this.state.sale.VatRegime == 7) {
			let netto = ((bruto - (this.unitBuyPrice * this.amount)) / this.vatMultiplier) + this.unitBuyPrice * this.amount;
			return bruto - netto;
		}
		return super.getVatFromBruto(bruto);
	}


	public setRowValues(row:SaleRow){
		row.Amount1 = this.amount1;
		row.Amount2 = this.amount2;
		row.TrackingNumber = this.trackingNumber;
		row.SizeTableEntryID = this.sizeTableEntryID;
		// Keep the relation in sync so the chosen size name shows immediately (before reload).
		row.SizeTableEntry = this.sizeEntries.find(e => e.ID === this.sizeTableEntryID) ?? null;
		super.setRowValues(row);
		if(this.ledgerAccountCode){
			row.LedgerAccountCode = this.ledgerAccountCode;
		}
		if (row.Product) {
			row.FostplusCode = row.Product.FostplusCode;
			row.UnitWeight = row.Product.Weight;
		}
	}
}